import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const portfolioImages = [
  { id: 1, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/4qmziyi2_GOS_1738.webp' },
  { id: 2, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/y3qzct84_GOS_1746.webp' },
  { id: 3, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/698u9xb4_GOS_1772.webp' },
  { id: 4, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/fh90n3fb_GOSM0307.webp' },
  { id: 5, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/5mepr2q7_GOSM0308.webp' },
  { id: 6, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/vm52wn5y_GOSM0312.webp' },
  { id: 7, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/nws3rfrq_GOSM0313.webp' },
  { id: 8, url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_pro-media-events/artifacts/345fgzoc_GOSM0316.webp' }
];

const Portfolio = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Transition automatique toutes les 5 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % portfolioImages.length);
    }, 5000); // 5 secondes

    return () => clearInterval(interval);
  }, []);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % portfolioImages.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? portfolioImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-100/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-100/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-6 py-2 rounded-full text-sm font-semibold mb-6">
            <ImageIcon className="h-4 w-4" />
            Portfolio
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Notre Travail
            <span className="bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent"> En Images</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez quelques-uns de nos meilleurs moments capturés lors d'événements exceptionnels.
          </p>
        </div>

        {/* Carrousel pleine largeur */}
        <div className="relative max-w-7xl mx-auto">
          {/* Image principale */}
          <div className="relative w-full h-[700px] rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
            {portfolioImages.map((image, index) => (
              <div
                key={image.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={image.url}
                  alt={`Portfolio ${image.id}`}
                  className="w-full h-full object-contain"
                />
              </div>
            ))}

            {/* Boutons de navigation */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Photo précédente"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Photo suivante"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Indicateurs de pagination */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {portfolioImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-8 bg-amber-500' 
                      : 'w-2 bg-white/60 hover:bg-white'
                  }`}
                  aria-label={`Aller à la photo ${index + 1}`}
                />
              ))}
            </div>

            {/* Compteur */}
            <div className="absolute top-6 right-6 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-semibold">
              {currentIndex + 1} / {portfolioImages.length}
            </div>
          </div>

          {/* Thumbnails */}
          <div className="flex gap-4 mt-6 overflow-x-auto pb-2">
            {portfolioImages.map((image, index) => (
              <button
                key={image.id}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentIndex 
                    ? 'ring-4 ring-amber-500 scale-105' 
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={image.url}
                  alt={`Thumbnail ${image.id}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-20">
          <p className="text-lg text-gray-600 mb-6 font-medium">
            Vous souhaitez voir plus de notre travail ?
          </p>
          <button 
            onClick={() => {
              const message = "Bonjour GOS MÉDIA! J'aimerais voir plus de photos de votre portfolio.";
              const url = `https://wa.me/+243999006868?text=${encodeURIComponent(message)}`;
              window.open(url, '_blank');
            }}
            className="group bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105"
          >
            Découvrir Plus de Projets
          </button>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
